import {Back, Linear, Power2, TimelineLite, TimelineMax, TweenMax} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Mental} from "../../game/Mental";
import {SoundManager} from "../../game/sound/SoundManager";
import {MentalSoundConfig} from "../../game/MentalSoundConfig";
import {MentalNormalSymbol} from "../../game/reel/symbols/MentalNormalSymbol";
import {MentalSymbolStates} from "../../game/reel/MentalSymbol";
import {MentalGameMode} from "../../game/server/MentalGameMode";
import {IMentalGameData} from "../../game/server/MentalData";

export interface Positionable {
    position: {
        x: number,
        y: number
    }
}

export interface ReelModelDelegate {
    createSymbol(symbolName: string, state: ReelModelState, position: number): Positionable;

    removeSymbol(symbolData: ReelModelSymbolData, state: ReelModelState): void;
}

export interface ReelModelSymbolData {
    name: string,
    positionable: Positionable,
    reelPosition: number
}

export enum ReelModelState {
    IDLE = "IDLE",
    START = "START",
    SPIN = "SPIN",
    STOP = "STOP",
    STOPPED = "STOPPED",
    NUDGE = "NUDGE"
}

export interface ReelModelStateEvent {
    reel: number;
    state: ReelModelState;
}

interface ReelPaddingData {
    paddingBefore: number;
    visibleSymbols: number[];
    paddingAfter: number[];
}

export class ReelModel {
    public animatedStepData = {
        y: 0
    }
    public stepDuration = 0.04;
    public readonly id: number;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     */
    public readonly events: APIEventSystem | undefined;
    private _stopTriggerTl: TimelineLite;
    private _stopIndex: number;
    private _stopSymbols: ReelModelSymbolData[] = [];
    private _reelMeta: ReelPaddingData = {
        paddingBefore: 1,
        visibleSymbols: [3, 2, 3, 2, 3],
        paddingAfter: [1, 2, 1, 2, 1],
    }
    private _stepSize: number = 198;
    private _spinning: boolean = false;
    private _currentSpinStep: TweenMax | undefined;
    private animationTimeScale = 1;
    private _stopTimeline: TimelineLite | undefined;
    private _stopping: boolean = false;
    private triggerStop: boolean = false;
    private _startedReverse: boolean;
    private masterSpinTimeline: TimelineLite | undefined;
    private _delegate: ReelModelDelegate;
    private _nudgeSteps: number = 0;

    constructor(id: number, delegate: ReelModelDelegate, triggerEvents: boolean = false) {
        this.id = id;
        this._delegate = delegate;
        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data: ReelModelStateEvent) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }
    }

    //Fake reel
    private _reelSet: string[] = ["M1", "L1"];

    set reelSet(value: string[]) {
        this._reelSet = value;
    }

    private _reelSetIndex: number = 0;

    get reelSetIndex(): number {
        return this._reelSetIndex;
    }

    set reelSetIndex(value: number) {
        this._reelSetIndex = value;
    }

    private _symbols: ReelModelSymbolData[] = [];

    get symbols(): ReelModelSymbolData[] {
        return this._symbols;
    }

    private _speed: number = this.animationTimeScale / this.stepDuration;

    get speed(): number {
        return this._speed;
    }

    set speed(value: number) {
        this._speed = value;
        this.animationTimeScale = value * this.stepDuration;
        this.masterSpinTimeline?.timeScale(this.animationTimeScale);
    }

    private _state: ReelModelState = ReelModelState.IDLE;

    get state(): ReelModelState {
        return this._state;
    }

    set state(value: ReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <ReelModelStateEvent>{reel: this.id, state: this._state});
        }
    }

    public init(symbols: string[]) {
        const totalCount = (this._reelMeta.paddingBefore) + this._reelMeta.visibleSymbols[this.id] + this._reelMeta.paddingAfter[this.id];

        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);


        while (symbols.length < totalCount) {
            const randomSym = this.getRandomSymbolInReelSet();
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}, adding random replacement from current reel set: ${randomSym}`);
            symbols.push(randomSym);
        }
        while (symbols.length > totalCount) {
            Logger.warn(`ReelModel.init: Symbol count mismatch in reel ${this.id}, removing symbol ${symbols.pop()}`);
        }

        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(symbols[i], i - this._reelMeta.paddingBefore, ReelModelState.IDLE);
            this._symbols.push(symbol);
            this._stopSymbols.push(symbol);
        }

        this.updatePosition();
    }

    public stop(stopSymbolNames: string[], reverse: boolean = false): TimelineLite {
        reverse = this._startedReverse;

        this.clearSymbols(this._stopSymbols);

        this._stopSymbols = [];
        for (let i: number = 0; i < stopSymbolNames.length; i++) {
            const symbol = this.createNewSymbol(stopSymbolNames[i], i - this._reelMeta.paddingBefore, ReelModelState.STOP);
            symbol.positionable.position.y = this._stepSize * -this._reelMeta.paddingBefore;
            this._stopSymbols.push(symbol);
        }

        this._stopTriggerTl = new TimelineLite({
            paused: true,
            onStart: () => {
                this.state = ReelModelState.STOP;
                this._spinning = false;
                this.triggerStop = true;
            },
            onComplete: () => {
                Mental.fireFrame.hideFireFrame(this.id);
                const tl: TimelineMax = new TimelineMax();
                tl.add(() => {
                    Mental.stickyScatter.hideAllOverlayOnReelStop(this.id);
                }, 0.3);
            }
        });

        this._stopTimeline = new TimelineLite({
            paused: true,
            onStart: () => {
                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;
            },
            onComplete: () => {
                this.state = ReelModelState.STOPPED;
                this._stopTimeline = undefined;
                this._stopping = false;
                this.state = ReelModelState.IDLE;
            }
        });

        const numberOfNormalSteps = this._reelMeta.visibleSymbols[this.id] + this._reelMeta.paddingBefore + this._reelMeta.paddingAfter[this.id];
        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step(reverse);
            this._stopTimeline.add(tween);
        }

        this._stopTimeline.add(this.bounce());

        this._stopTriggerTl.to(this, {duration: this._stopTimeline.totalDuration()});

        return this._stopTriggerTl.play(0);
    }

    public start(reverse: boolean = false): TimelineLite {
        this._startedReverse = reverse;
        const tl: TimelineLite = new TimelineLite({
            onStart: () => {
                this.state = ReelModelState.START;
                this._stopSymbols = [];
                this._nudgeSteps = 0;
            },
            onComplete: () => {
                this.startSpin(reverse);
            }
        });

        const tween = this.step(reverse);
        tween.vars.ease = Back.easeIn.config(3);
        tween.totalDuration(this.stepDuration * 8);

        tl.add(tween);

        return tl;
    }

    public bounce(bounceFactor: number = 0.2, durationFactor: number = 1, reverse: boolean = false): TimelineLite {
        const tl = new TimelineLite();
        const overshoot = TweenMax.to(this.animatedStepData, this.stepDuration * durationFactor, {
            y: reverse ? -this._stepSize * bounceFactor : this._stepSize * bounceFactor,
            ease: Power2.easeOut,
            onUpdate: () => this.updatePosition(),
        });

        const back = TweenMax.to(this.animatedStepData, this.stepDuration * durationFactor, {
            y: 0,
            ease: Power2.easeOut,
            onUpdate: () => this.updatePosition(),
        });

        tl.add([
            overshoot,
            () => this.playLandingSounds()
        ]);
        tl.add(back);

        return tl;
    }

    public getAllVisibleStopSymbols(): ReelModelSymbolData [] {
        return this._stopSymbols.slice(this._reelMeta.paddingBefore + this._nudgeSteps, this._reelMeta.paddingBefore + this._reelMeta.visibleSymbols[this.id] + this._nudgeSteps);
    }

    /**
     * function to get reel Visible symbol + enhancer symbol
     */
    public getAllVisibleStopSymbolsWithEnhancerSymbols(): ReelModelSymbolData [] {
        return this._stopSymbols.slice(this._reelMeta.paddingBefore + this._nudgeSteps, this._stopSymbols.length - 1);
    }

    public getStopSymbolAt(symbolIndex: number): ReelModelSymbolData {
        return this._stopSymbols[symbolIndex + this._nudgeSteps + this._reelMeta.paddingBefore];
    }

    public addStopSymbolAt(symbolName: string, index: number): ReelModelSymbolData {
        this._symbols[index + this._nudgeSteps + this._reelMeta.paddingBefore] = this.createNewSymbol(symbolName, index, ReelModelState.IDLE);
        return this._symbols[index + this._nudgeSteps + this._reelMeta.paddingBefore];
    }

    // dead symbol
    public addStopSymbolInStopSymbol(symbolName: string, index: number): ReelModelSymbolData {
        this._stopSymbols[index + this._nudgeSteps + this._reelMeta.paddingBefore] = this.createNewSymbol(symbolName, index, ReelModelState.IDLE);
        return this._stopSymbols[index + this._nudgeSteps + this._reelMeta.paddingBefore];
    }

    public getReelSymbolAt(symbolIndex: number): ReelModelSymbolData {
        return this._symbols[symbolIndex + this._nudgeSteps + this._reelMeta.paddingBefore];
    }

    public updateReelSymbolAt(symbolIndex: number, symbolName: string): ReelModelSymbolData {
        this._symbols[symbolIndex + this._nudgeSteps + this._reelMeta.paddingBefore] = this.createNewSymbol(symbolName, symbolIndex, ReelModelState.IDLE);
        this._stopSymbols = this._symbols;
        return this._symbols[symbolIndex + this._nudgeSteps + this._reelMeta.paddingBefore];
    }

    public nudge(reverse: boolean = false, stepSize: number = 198): TweenMax {
        const tween = this.nudgeStep(reverse, stepSize);
        tween.eventCallback("onStart", () => {
            this._nudgeSteps += reverse ? 1 : -1;
            this.state = ReelModelState.NUDGE;
            this.onAnimationStart(tween, reverse);
            this._stepSize = stepSize;
        });

        return tween;
    }

    private onReelStateChange(data: ReelModelStateEvent): void {
        this.events?.trigger(data.state, data);
    }

    private getNextSymbolName(nextSymbolPosition: number): string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this._reelSet.length + nextIndex;
        }
        if (nextIndex >= this._reelSet.length) {
            nextIndex = nextIndex - this._reelSet.length;
        }
        return this._reelSet[nextIndex];
    }

    private getNextSymbol(position: number, state: ReelModelState): ReelModelSymbolData {
        if (this._stopping && this._stopSymbols.length > 0) {
            const symbol = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }
        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position, state)
    }

    private createNewSymbol(name: string, position: number, state: ReelModelState): ReelModelSymbolData {
        const symbol: ReelModelSymbolData = {
            name: name,
            positionable: this._delegate.createSymbol(name, state, position),
            reelPosition: position
        }
        return symbol;
    }

    private getRandomSymbolInReelSet(): string {
        const randomIndex = Math.floor(Math.random() * this._reelSet.length);
        return this._reelSet[randomIndex];
    }

    private clearSymbols(symbols: ReelModelSymbolData[]): void {
        let symbol;
        while (symbol = symbols.pop()) {
            this._delegate.removeSymbol(symbol, this.state);
        }
    }

    private startSpin(reverse: boolean = false) {
        this._spinning = true;
        this.state = ReelModelState.SPIN;
        this.masterSpinTimeline = new TimelineLite();
        this.masterSpinTimeline.add(this.spin(reverse));
    }

    private spin(reverse: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                if (this._spinning) {
                    this.masterSpinTimeline?.add(this.spin(reverse));
                    //this.spin(reverse);
                }
            }
        })
        tl.add(this.step(reverse));
        return tl;
    }

    private step(reverse: boolean = false): TweenMax {
        const tween: TweenMax = TweenMax.to(this.animatedStepData, this.stepDuration, {
            y: reverse ? -this._stepSize : this._stepSize,
            ease: Linear.easeNone,
            onStart: () => this.onAnimationStart(tween, reverse),
            onUpdate: () => this.updatePosition(),
            onComplete: () => this.onAnimationComplete(reverse)
        });
        return tween;
    }

    private onAnimationStart(tween: TweenMax, reverse: boolean = false) {
        this._currentSpinStep = tween;
        if (this.state !== ReelModelState.NUDGE) {
            this.addSymbol(reverse);
        }
    }

    private onAnimationComplete(reverse: boolean = false) {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this._stopTimeline?.play(0);
        }

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse: boolean) {
        if (this._stopping) {
            if (reverse) {
                this._stopIndex++;
                if (this._stopIndex >= this._stopSymbols.length) {
                    this._stopIndex = 0;
                }
            } else {
                this._stopIndex--;
                if (this._stopIndex < 0) {
                    this._stopIndex = this._stopSymbols.length - 1;
                }
            }
        } else {
            if (reverse) {
                this._reelSetIndex++;
                if (this._reelSetIndex >= this._reelSet.length) {
                    this._reelSetIndex = 0;
                }
            } else {
                this._reelSetIndex--;
                if (this._reelSetIndex < 0) {
                    this._reelSetIndex = this._reelSet.length - 1;
                }
            }
        }
    }

    private addSymbol(reverse: boolean) {
        let newSymbols = [];
        if (reverse) {
            const newSymbolPosition = this._symbols[this._symbols.length - 1].reelPosition + 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
            //newSymbols.push(this.createNewSymbol(this.getNextSymbolName(newSymbolPosition), newSymbolPosition, this.state));
        } else {
            const newSymbolPosition = this._symbols[0].reelPosition - 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
        }

        this._symbols = this._symbols.concat(newSymbols);
        this._symbols.sort((a: any, b: any) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });
    }

    private removeSymbol() {
        for (let i: number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition >= this._reelMeta.visibleSymbols[this.id] + (this._reelMeta.paddingAfter[this.id])) {
                //Remove from bottom
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol, this.state);
            }
            if (symbol.reelPosition < -this._reelMeta.paddingBefore) {
                //Remove from top
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol, this.state);
            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            const basePos = this._stepSize * symbol.reelPosition;
            symbol.positionable.position.y = basePos + this.animatedStepData.y;
            if ((this.id === 1 || this.id === 3) && this._spinning && symbol.name === "S2") {
                Mental.scatterAnticipation.playPassByAnim(this.id);
            }
        }
    }

    private nudgeStep(reverse: boolean = false, stepSize: number): TweenMax {
        const tween: TweenMax = TweenMax.to(this.animatedStepData, this.stepDuration, {
            y: reverse ? -this._stepSize : this._stepSize,
            ease: Linear.easeNone,
            onStart: () => this.onAnimationStart(tween, reverse),
            onUpdate: () => this.updatePosition(),
            onComplete: () => {
                this.onAnimationComplete(reverse);
                this._stepSize = 198;
            }
        });
        return tween;
    }

    private playLandingSounds(): void {
        // #116
        if (this.id === 4) {
            SoundManager.stopSoundEffect(MentalSoundConfig.REEL_SPIN);
        }
        SoundManager.playSoundEffect(MentalSoundConfig.REELS_DEFAULT_BOUNCE[this.id]);

        // #66
        const visibleSymbols: ReelModelSymbolData[] = this.getAllVisibleStopSymbols(),
            gameData: IMentalGameData = Mental.model.gameData,
            ffData: boolean[][] = (gameData.mode === MentalGameMode.NORMAL ? gameData.fireFrames : gameData.stickyFires);
        let symName: string;
        for (let i: number = 0; i < visibleSymbols.length; i++) {
            symName = visibleSymbols[i].name;

            // #216, FF check added
            if (symName === "W") {
                if (!ffData[this.id][i]) {
                    (visibleSymbols[i].positionable as MentalNormalSymbol).setState(MentalSymbolStates.LANDING);
                    SoundManager.playSoundEffect(MentalSoundConfig.WILD_LANDING);
                }
            }
            // #354
            else if (symName === "S") {
                this.playScatterLandingSounds(gameData);
            } else if (symName === "S2") {
                this.playScatter2LandingSounds(gameData);
            }
        }
    }

    // #354
    private playScatterLandingSounds(gameData: IMentalGameData): void {
        const scatterPositions: number[] = gameData.scatterPositions;

        if (scatterPositions[this.id] > -1) {
            if (this.id === 0) {
                SoundManager.playSoundEffect(MentalSoundConfig.SCATTER_SCORPION[0]);
            } else if (this.id === 2 && scatterPositions[0] > -1) {
                SoundManager.playSoundEffect(MentalSoundConfig.SCATTER_SCORPION[1]);
            } else if (this.id === 4 && scatterPositions[0] > -1 && scatterPositions[2] > -1) {
                SoundManager.playSoundEffect(MentalSoundConfig.SCATTER_SCORPION[2]);
            }
        }
    }

    // #354
    private playScatter2LandingSounds(gameData: IMentalGameData): void {
        const s2SymIdInReels: number = gameData.scatter2Position[this.id],
            mode: string = gameData.mode; // #370
        if (s2SymIdInReels > -1 && gameData.reels[this.id][s2SymIdInReels + 1] === "S2") {
            if (this.id % 2 === 1) {
                SoundManager.playSoundEffect((mode === MentalGameMode.NORMAL ? MentalSoundConfig.SCATTER_SPIDER[0] : MentalSoundConfig.SCATTER_SPIDER[1]));
            }
        }
    }
}